import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Plane, Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/password-reset/request`, { email });
      setSent(true);
      
      // MVP: Show token in toast for simulation
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        toast.success('Lien de réinitialisation généré !');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00em0wLTEwYzAtMi4yIDEuOC00IDQtNHM0IDEuOCA0IDQtMS44IDQtNCA0LTQtMS44LTQtNHptMC0xMGMwLTIuMiAxLjgtNCA0LTRzNCAxLjggNCA0LTEuOCA0LTQgNC00LTEuOC00LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-foreground">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg" 
              alt="Kacos Logo" 
              className="h-14 w-auto"
            />
          </Link>
          
          <h1 className="font-heading text-4xl font-bold mb-4">
            Récupération de compte
          </h1>
          <p className="text-lg text-foreground max-w-md">
            Pas de panique ! Nous allons vous aider à récupérer l'accès à votre compte en quelques étapes simples.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg" 
              alt="Kacos Logo" 
              className="h-10 w-auto"
            />
          </Link>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <Link to="/login" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </Link>
              <CardTitle className="font-heading text-2xl">Mot de passe oublié</CardTitle>
              <CardDescription>
                {sent 
                  ? "Vérifiez votre email pour le lien de réinitialisation"
                  : "Entrez votre adresse email pour recevoir un lien de réinitialisation"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {!sent ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Adresse email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12"
                        required
                        data-testid="forgot-email"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 btn-primary"
                    disabled={loading}
                    data-testid="forgot-submit"
                  >
                    {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
                  </Button>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <p className="text-sm">
                      Si un compte existe avec cette adresse, un lien de réinitialisation a été envoyé.
                    </p>
                  </div>

                  {/* MVP Simulation - Token Display */}
                  {resetToken && (
                    <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
                            Mode simulation (MVP)
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">
                            En production, ce lien serait envoyé par email. Pour tester, utilisez le lien ci-dessous :
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => navigate(`/reset-password/${resetToken}`)}
                            data-testid="go-reset-btn"
                          >
                            Réinitialiser mon mot de passe
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setSent(false); setResetToken(''); }}
                      className="text-sm text-primary hover:underline"
                    >
                      Renvoyer le lien
                    </button>
                  </div>
                </div>
              )}

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Vous vous souvenez de votre mot de passe ?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Se connecter
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
